import React, { useEffect, useState } from 'react';
import './Commandeclient.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ★ RÉGLAGES LARGEURS COLONNES — ajuste les valeurs px selon tes besoins
// Clés = noms bruts de la base (avant tout renommage)
const COLUMN_WIDTHS = {
  'Numero de commande':       '140px',
  'Numero de ligne':           '90px',
  'Client':                   '140px',
  'Item':                     '160px',
  'Description':              '250px',
  'Date de commande':         '120px',
  'Date de livraison prevue': '140px',
  'Quantite commandee':        '120px',
  'Quantité commandée':        '120px',
  'Quantite livree':           '110px',
  'Quantité livrée':           '110px',
  'RAL':                        '70px',
  'Statut paiement':           '130px',
  'Horizon':                    '90px',
  'Fournisseur':               '160px',
  'Approvisionneur':           '150px',
  'Statut':                    '100px',
};
// Toute colonne absente du mapping reçoit cette largeur par défaut
const DEFAULT_WIDTH = '120px';

// Largeur totale = somme des colonnes (force le navigateur à respecter le layout fixe)
const calcTotalWidth = (cols) =>
  cols.reduce((s, c) => s + parseInt(COLUMN_WIDTHS[c] || DEFAULT_WIDTH, 10), 0);

// ★ Présentation des colonnes
const COLONNES_NUM   = ['Numero de ligne', 'Quantite commandee', 'Quantité commandée', 'Quantite livree', 'Quantité livrée'];
const COLONNES_MONO  = ['Numero de commande', 'Item', 'Date de commande', 'Date de livraison prevue', 'RAL'];
const COLONNES_PILL  = ['Statut', 'Statut paiement', 'Horizon'];

// Couleur d'une pastille selon le texte (sans accent, minuscules)
const normaliser = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const toneValeur = (val) => {
  const v = normaliser(val);
  if (!v) return null;
  if (/(retard|rupture|bloque|impaye|annule|ko|urgent|depasse)/.test(v)) return 'danger';
  if (/(attente|partiel|en cours|a venir|acompte|semaine|j\+|proche)/.test(v)) return 'warn';
  if (/(livre|paye|regle|termine|clos|ok|solde|expedie|complet)/.test(v)) return 'ok';
  return 'neutral';
};

const IconeLoupe = () => (
  <svg className="cc-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
  </svg>
);

const FILTRES = [
  { name: 'commandeID',    label: 'N° commande',    placeholder: 'N° commande…' },
  { name: 'item',          label: 'Item',           placeholder: 'Item…' },
  { name: 'client',        label: 'Client',         placeholder: 'Client…' },
  { name: 'dateLivraison', label: 'Date livraison', placeholder: 'Date livraison…' },
];

const Commandeclient = () => {
  const [commandes, setCommandes]        = useState([]);
  const [colonnesAAfficher, setColonnes] = useState([]);
  const [loading, setLoading]            = useState(true);
  const [filtres, setFiltres] = useState({ commandeID: '', item: '', client: '', dateLivraison: '' });

  useEffect(() => {
    document.title = 'MYSSA – Cmde Client';
  }, []);

  useEffect(() => {
    fetch(`${API}/base_de_donnee_cmde_client`)
      .then(r => r.json())
      .then(data => {
        if (data.length > 0) {
          const formatted = data.map(row => {
            const r = { ...row };
            ['Date de commande', 'Date de livraison prevue'].forEach(col => {
              if (r[col]) {
                const s = String(r[col]).split('T')[0].split('-');
                if (s.length === 3) r[col] = `${s[2]}/${s[1]}/${s[0]}`;
              }
            });
            return r;
          });
          setCommandes(formatted);
          setColonnes(Object.keys(data[0]).filter(c => c !== 'ID' && c !== 'Client_id'));
        }
        setLoading(false);
      })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  const handleReset = () => setFiltres({ commandeID: '', item: '', client: '', dateLivraison: '' });

  const filtered = commandes.filter(cmd =>
    cmd['Numero de commande']?.toString().toLowerCase().includes(filtres.commandeID) &&
    cmd['Item']?.toLowerCase().includes(filtres.item) &&
    cmd['Client']?.toLowerCase().includes(filtres.client) &&
    cmd['Date de livraison prevue']?.toLowerCase().includes(filtres.dateLivraison)
  );

  const exportToExcel = () => {
    const ws  = XLSX.utils.json_to_sheet(filtered.map(r => ({ ...r })));
    const wb  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Commandes clients');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `commandes_clients_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const totalWidth      = calcTotalWidth(colonnesAAfficher);
  const nbFiltresActifs = Object.values(filtres).filter(Boolean).length;
  const nbCommandes     = new Set(filtered.map(c => c['Numero de commande'])).size;

  const cellule = (cmd, col, j) => {
    const val = cmd[col];
    if (COLONNES_PILL.includes(col)) {
      const tone = toneValeur(val);
      return (
        <td key={j} title={val ?? ''}>
          {tone ? <span className={`cc-badge cc-badge-${tone}`}>{val}</span> : <span className="cc-muted">—</span>}
        </td>
      );
    }
    const classes = [
      COLONNES_NUM.includes(col) ? 'num cc-mono' : '',
      COLONNES_MONO.includes(col) ? 'cc-mono' : '',
      col === 'Numero de commande' ? 'cc-strong' : '',
      col === 'Client' ? 'cc-client' : '',
    ].filter(Boolean).join(' ');
    return <td key={j} className={classes} title={val ?? ''}>{val}</td>;
  };

  return (
    <div className="cc-page">
      {/* ★ En-têtes et données centrés dans le tableau (prioritaire sur Commandeclient.css,
            y compris la classe "num" qui aligne les quantités à droite) */}
      <style>{`
        .cc-table th, .cc-table th.num,
        .cc-table td, .cc-table td.num { text-align: center !important; vertical-align: middle; }
      `}</style>
      <div className="cc-container">

        {/* ── En-tête ── */}
        <div className="cc-header">
          <div className="cc-title-row">
            <span className="cc-accent" />
            <h1 className="cc-title">Commandes clients</h1>
            <span className="cc-chip-dept">DRC</span>
          </div>
       
        </div>

        {/* ── Filtres ── */}
        <div className="cc-filters">
          {FILTRES.map(({ name, label, placeholder }) => (
            <div key={name} className="cc-search">
              <IconeLoupe />
              <input
                type="text"
                className={`cc-input ${filtres[name] ? 'active' : ''}`}
                placeholder={placeholder}
                aria-label={`Filtrer par ${label}`}
                value={filtres[name]}
                onChange={e => setFiltres({ ...filtres, [name]: e.target.value.toLowerCase() })}
              />
              {filtres[name] && (
                <button type="button" className="cc-clear" aria-label={`Effacer ${label}`} onClick={() => setFiltres({ ...filtres, [name]: '' })}>×</button>
              )}
            </div>
          ))}

          <button type="button" className="cc-btn cc-btn-ghost" onClick={handleReset} disabled={nbFiltresActifs === 0}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
            Réinitialiser{nbFiltresActifs ? ` (${nbFiltresActifs})` : ''}
          </button>

          <button type="button" className="cc-btn cc-btn-primary" onClick={exportToExcel} disabled={filtered.length === 0}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>
            Export Excel
          </button>
        </div>

        {/* ── Tableau ── */}
        <div className="cc-card cc-table-card">
          <div className="cc-table-head">
            <div className="cc-table-head-left">
              <span className="cc-table-title">Lignes de commande</span>
              <span className="cc-count">
                {filtered.length}
                {filtered.length !== commandes.length && <span className="cc-count-total"> / {commandes.length}</span>}
              </span>
            </div>
            <div className="cc-table-head-right">
              <span className="cc-meta">{nbCommandes} commande{nbCommandes > 1 ? 's' : ''}</span>
            </div>
          </div>

          <div className="cc-table-scroll">
            {loading ? (
              <div className="cc-empty">Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="cc-empty">
                {commandes.length === 0 ? 'Aucune commande client.' : 'Aucune ligne ne correspond aux filtres.'}
              </div>
            ) : (
              /* tableLayout fixed + largeur totale = colonnes figées (★ COLUMN_WIDTHS en haut du fichier) */
              <table className="cc-table" style={{ width: `${totalWidth}px` }}>
                <colgroup>
                  {colonnesAAfficher.map(c => (
                    <col key={c} width={COLUMN_WIDTHS[c] || DEFAULT_WIDTH} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {colonnesAAfficher.map(c => (
                      <th key={c} className={COLONNES_NUM.includes(c) ? 'num' : ''} title={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((cmd, i) => (
                    <tr key={i}>
                      {colonnesAAfficher.map((col, j) => cellule(cmd, col, j))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Commandeclient;